/**
 * OAuth 2.0 authorization-code flow with PKCE (S256) against KorOS identity.
 *
 * The verifier, state and nonce live in sessionStorage only for the round trip
 * to the identity provider and are deleted as soon as the callback consumes
 * them. Nothing here is a long-lived secret.
 */
import { readJson, removeStorage, writeJson } from '@/lib/storage';

const PENDING_KEY = 'kb.auth.pending';
const MAX_AGE_MS = 15 * 60_000;

/**
 * Why this round trip to KorOS happens. Absent = a normal sign-in that
 * creates the Build session. `handover_confirm` = the owner confirming a
 * "Move to KorOS" handover: the code goes to the handover, and the current
 * session is kept as it is.
 */
export type AuthPurpose =
  | { kind: 'handover_confirm'; project_id: string }
  /**
   * `koros_organizations` = the owner refreshing the "Move to KorOS"
   * organisations picker: the code goes to the handover's
   * `organizations/refresh`, and the current session is kept as it is.
   */
  | { kind: 'koros_organizations'; project_id: string }
  /**
   * `django_admin` = a superuser opening Django admin: the code goes to
   * `POST /auth/django-admin-session` (a Django session, never a Build one)
   * and the browser then loads `next` (a /django-admin/ path) from the server.
   */
  | { kind: 'django_admin'; next: string }
  /**
   * `backoffice` = Build staff opening the back office: the code goes to
   * `POST /auth/backoffice-session` (a Django session, never a Build one) and
   * the browser then loads `next` (a /backoffice/ path) from the server.
   */
  | { kind: 'backoffice'; next: string };

export type PendingSignIn = {
  state: string;
  nonce: string;
  code_verifier: string;
  redirect_uri: string;
  return_to: string;
  created_at: number;
  purpose?: AuthPurpose;
};

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function randomToken(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export async function codeChallengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

export function savePendingSignIn(pending: PendingSignIn): void {
  writeJson(PENDING_KEY, pending, 'session');
}

/** Read and delete the pending sign-in (single use). Null if missing or stale. */
export function consumePendingSignIn(): PendingSignIn | null {
  const pending = readJson<PendingSignIn>(PENDING_KEY, 'session');
  removeStorage(PENDING_KEY, 'session');
  if (!pending || Date.now() - pending.created_at > MAX_AGE_MS) return null;
  return pending;
}

/** Only same-app paths are allowed as post-sign-in destinations. */
export function safeReturnPath(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return '/home';
  }
  if (value.startsWith('/signin') || value.startsWith('/auth/')) return '/home';
  return value;
}
